/**
 * D17d: first-class name + mobile capture for phone-ins / walk-ins — always
 * visible on the quote step for jobs with no customer yet, so Helen sees
 * where the name goes BEFORE tapping Accept (R14 discoverability). The name
 * still gates persistence; skipping it stays an honest price check.
 * Labels 'Customer name' / 'Customer phone' are net-locked — keep verbatim.
 *
 * T540 (533): the journey date/time live here too. A call-in captured NO date
 * anywhere in the kiosk, so a phone booking was quoted, recorded and SMS'd with
 * no record of WHEN the job is — and for a transfer business the time IS the
 * product. Optional by design (docs/round15-prd.md:70, "add an optional 'When
 * is the job?'"): Helen is on a live call and may not have the date yet, and a
 * fabricated date poisons the dispatcher seed the field exists for. But the
 * absence is VISIBLE, never silent, per the advisory precedent below and at
 * DriverDetails' pickupMissing prompt — a silently-optional field is the same
 * defect class this task exists to fix.
 *
 * Labels 'Journey date' / 'Journey time' are net-locked too. `getByLabel('Date')`
 * is already taken by the SITE form and the portal specs, so these must stay
 * distinct or a bare-'Date' lookup goes ambiguous across projects.
 *
 * T660 M5 (P0-9/P0-10): the OPTIONAL operator-typed email, behind a disclosure.
 * It answers Q55 — until now there was no operator-typed email control anywhere
 * in the kiosk, so `customers.email` could only ever be filled by the public
 * form. It writes `job.operatorEmail`, a store field with NO other writer; it
 * must never be wired to `job.customerEmail`, which `sourceFromInquiry`
 * prefills from the PUBLIC form (the laundering hole M8 closed, pinned by
 * test/client/capture-email-laundering.spec.ts).
 *
 * Board 2089 M3 (R-2, R-3, H-4): the COUNTRY the number is read under is now a
 * control beside the number, and the number echoes back as it will be STORED.
 * Helen prices airport runs for visitors, so a `07…` typed under Ireland is a
 * real Tuesday and used to save as no number at all, silently. The picker
 * defaults to Ireland, a typed `+` still overrides it (the library's rule), and
 * the echo names the PARSED number's country rather than the picker's so the
 * override is visible rather than merely tolerated.
 */
import type { RefObject } from 'preact';
import { FormField } from '@ecmb/ui';
import type { JobState } from '../store/state.js';
import {
  listPhoneCountries,
  parseCustomerPhone,
  formatPhoneWithCountry,
  phoneCountryName,
} from '../../shared/phone.js';

/** The date/time pair, patched straight into the store (never local state):
 *  the four Accept/Park/override/record paths each lift name+phone separately,
 *  and a fifth field lifted at only three of them would drop on the fourth. */
export type WhenPatch = {
  journeyDate?: string | null;
  journeyTime?: string | null;
  /** ⭐ board 2871 - the Quote screen's Return date / Return time, the same plain patch. */
  returnDraft?: JobState['returnDraft'];
};

export type CallInCaptureProps = {
  name: string;
  phone: string;
  /** The ISO country the typed number is READ UNDER (board 2089 M3). A plain
   *  string rather than `PhoneCountry`: it is a `<select>` value and the store
   *  carries it as one, and `parseCustomerPhone` is the thing that decides
   *  whether it names a country (an unknown value reads as Ireland, never a
   *  refusal). REQUIRED, so a host cannot mount the picker with nothing
   *  selected and let the browser choose the first option for Helen. */
  phoneCountry: string;
  onPhoneCountry: (v: string) => void;
  /** T660 M5 — the OPERATOR-typed address (job.operatorEmail), never
   *  job.customerEmail. See the store field's own header for why the two must
   *  not be merged. */
  email: string;
  journeyDate: string | null;
  journeyTime: string | null;
  nameInputRef?: RefObject<HTMLInputElement>;
  onName: (v: string) => void;
  onPhone: (v: string) => void;
  onEmail: (v: string) => void;
  onWhen: (patch: WhenPatch) => void;
};

export function CallInCapture({
  name,
  phone,
  phoneCountry,
  onPhoneCountry,
  email,
  journeyDate,
  journeyTime,
  nameInputRef,
  onName,
  onPhone,
  onEmail,
  onWhen,
}: CallInCaptureProps) {
  const echo = phoneEcho(phone, phoneCountry);
  return (
    <div class="customer-capture">
      {/* ⭐ BOARD 2089 M3 — NAME, COUNTRY AND PHONE ARE ALL `FormField` NOW, AND
          THE THREE MOVE TOGETHER OR NOT AT ALL. The width lives on the library's
          own wrapper (`.field .login-input { width: 100% }`,
          `packages/ui/styles/components.css`; `app.css` records why the
          per-surface stand-in was deleted), `.customer-capture` sets only a
          `max-width`, and a RAW `.login-input` has no width rule at all — so a
          library `<select>` above two raw inputs would render one full-width
          control over two 252px ones. Lifting phone alone would leave `Customer
          name` narrow directly above them, which is why the name came too.
          ⚠️ THE LABELS ARE UNCHANGED AND STILL NET-LOCKED (`Customer name`,
          `Customer phone`): this is a markup lift, not a rename, and
          `getByLabel('Customer phone')` still resolves to exactly one control.
          ⚠️ CENSUS-NEUTRAL for name and phone — a label and a control counted 1+1
          by hand and count 1+1 inside a `FormField`, whose inner elements carry no
          adoption stamp of their own (the attribute is named in
          `packages/ui/src/FormField.tsx`; writing it here would red
          `adoption-census.spec.tsx`, whose reader does not strip comments). The
          COUNTRY field is the whole of the +1/+1 in
          `test/client/lib/adoption-debt.ts`'s two Quote rows.
          ⚠️ The journey date/time below stay raw: they sit under the email
          disclosure rather than in this block, and re-cutting them is not this
          milestone's. */}
      {/* ⛔⛔ T1749 M9 rework 2 (Captain's four-gate check) — THE STANDING PARAGRAPH
          BECAME THIS FIELD'S HINT, and one clause of it survived.

          It read, as a `p.capture-hint` ABOVE the label: "Who is this quote for?
          Add the name to save it. Without a name it stays a price check."
          · DROPPED "Who is this quote for?" — the label under it says `Customer
            name`, so the question was the label asked twice.
          · DROPPED "Add the name to save it." — an instruction to fill in the box
            it sits on, which is what a labelled empty box already says.
          · KEPT the CONSEQUENCE, because no label on this screen states it: a
            nameless quote is not saved, it is a price check — a real state of this
            tool (`quote-flow.spec.tsx`'s `doneOutcome: 'price-check'`,
            `manual-quote.spec.tsx`'s hard gate), and Helen cannot infer it from
            anything else on the screen.
          ⛔ A HINT, NOT A PARAGRAPH: it carries an id and the control points at
          it, so a reader who lands on the box HEARS the consequence. As a standing
          paragraph it was announced to nobody who tabbed into the field.
          ⭐ AND IT IS THE LIBRARY'S HINT SLOT SINCE BOARD 2089 M3, NOT A HAND-ROLLED
          `p.field-hint`. M9 wrote the markup "`FormField`'s, byte for byte" and
          said the adoption was a lift waiting for a milestone that owns the block;
          this is that milestone. `hintIdFor('capture-name')` is `capture-name-hint`
          and `describedByFor` wires it, so the rendered ids and the
          `aria-describedby` are the ones that were there before.
          ⚠️ `.capture-hint` IS NOT RETIRED WITH IT: the `<details>` summary below
          still wears it, and `customer-email-correction.spec.tsx` pins the scoped
          `summary.capture-hint` rule. */}
      <FormField
        id="capture-name"
        label="Customer name"
        type="text"
        value={name}
        elementRef={nameInputRef}
        onInput={onName}
        hint="Without a name it stays a price check."
      />
      {/* ⛔⛔ BOARD 2089 M3 (R-2) — THE COUNTRY SITS BEFORE THE NUMBER, NOT AFTER
          IT, because it is what the number is READ UNDER: a reader who tabs into
          the phone box has already passed the control that decides how their
          digits are interpreted.
          ⛔ THE LABEL IS `Country`, NOT `Phone country`. Playwright matches an
          accessible name by SUBSTRING, and eight e2e specs call
          `getByLabel('Customer phone')`; a label containing that phrase would go
          ambiguous in every one of them. (testing-library matches exactly, so the
          vitest side would have stayed green — the harness split
          `CallInCapture`'s email comment already records.)
          ⚠️ THE OPTIONS ARE DERIVED, never a hand list: `listPhoneCountries()`
          reads the same `libphonenumber-js` metadata `parseCustomerPhone` parses
          with, so the picker can only ever offer a country the parser accepts.
          Memoised here the way the source list memoises itself — the map is ~250
          objects and the input is static. */}
      <FormField
        kind="select"
        id="capture-phone-country"
        label="Country"
        value={phoneCountry}
        options={countryOptions()}
        onInput={onPhoneCountry}
      />
      <FormField
        id="capture-phone"
        label="Customer phone"
        type="tel"
        value={phone}
        onInput={onPhone}
        /* ⛔ THE ECHO IS A SIBLING, NOT THE `hint` SLOT, AND POINTED AT BY HAND.
           `FormField`'s hint renders a plain `<p class="field-hint">` with no
           `role="status"`, so a value that changes under the reader's fingers
           would never be announced. This field's description is therefore an
           EXTRA id (`describedBy`), which is exactly what that prop exists for.
           ⚠️ The list is EMPTY when there is no echo: `describedByFor` answers
           `undefined` for an empty list, and an `aria-describedby` pointing at an
           id that is not on the page is a live attribute describing nothing. */
        describedBy={echo === null ? [] : ['capture-phone-echo']}
      />
      {/* ⛔⛔ ADR4-D10: A REFUSED NUMBER IS STATED AT THE CONTROL, NEVER IN A
          BANNER, and it never blocks — the phone never closes. One line does both
          jobs: it shows the number as it WILL BE STORED when it parses, and says
          why it will not be when it does not. */}
      {echo !== null && (
        <p class="field-hint" role="status" id="capture-phone-echo">{echo}</p>
      )}
      {/* T660 M5 / P0-9 — the optional operator-typed email, BEHIND A DISCLOSURE.
          Native <details>, closed by default: it costs one summary line on a
          screen whose hard constraint is "no more buttons", and no JS state, and
          the keyboard and screen-reader behaviour is the platform's rather than
          ours. The input is not laid out at all until Helen asks for it, which
          is what keeps this off an already-tight fold.

          IT DID NEED ONE CSS RULE, contrary to what this comment claimed: a
          <summary> is a touch target, and the live ux-review measured this one
          at 606x30, under the 44px floor. `summary.capture-hint` in app.css
          pads it; the class on the summary below is what that rule selects, so
          do not drop it (pinned in test/client/operator-email-capture.spec.tsx).

          A REAL <label>, never a placeholder (WCAG 3.3.2) — the placeholder
          disappears on the first keystroke and is invisible to some AT, and this
          field decides where a live payable deposit link is sent.

          'Customer email' and NOT 'Email': the send picker renders an `Email`
          RADIO, and the two lookups differ by harness — Playwright matches an
          accessible name by SUBSTRING, testing-library matches a label EXACTLY —
          so a bare 'Email' here would be safe in one net and ambiguous in the
          other. The distinct wording also matches the two labels above it. */}
      <details>
        <summary class="capture-hint">Add an email address (optional)</summary>
        <label class="field-label" for="capture-email">Customer email</label>
        <input
          id="capture-email"
          class="login-input"
          type="email"
          autocomplete="off"
          value={email}
          onInput={(e) => onEmail((e.target as HTMLInputElement).value)}
        />
        {/* ⚠️ T1749 M8 round 3 (impeccable C6) — `.provenance-line`, NOT a hint.
            §3.1 assigns "a caution to weigh" to the gold provenance treatment,
            and this is one: it says where the address must COME FROM and what
            typing it here commits to (the customer's record, and every future
            quote). A hint says what shape an answer takes; this says what the
            answer means. */}
        <p class="provenance-line">
          Only add an address the customer gave you. It is saved to their record and future quotes go
          there.
        </p>
      </details>
      <label class="field-label" for="capture-journey-date">Journey date</label>
      <input
        id="capture-journey-date"
        class="login-input"
        type="date"
        value={journeyDate ?? ''}
        onInput={(e) => onWhen({ journeyDate: (e.target as HTMLInputElement).value || null })}
      />
      <label class="field-label" for="capture-journey-time">Journey time</label>
      <input
        id="capture-journey-time"
        class="login-input"
        type="time"
        value={journeyTime ?? ''}
        onInput={(e) => onWhen({ journeyTime: (e.target as HTMLInputElement).value || null })}
      />
      {whenMissing(journeyDate) && (
        <p class="field-hint" role="status">
          No date yet. Add it when the customer says, so the job sheet tells the driver when to be there.
        </p>
      )}
    </div>
  );
}

/** ADVISORY only, never blocks (Q-A1, Captain 2026-07-28: Helen is on a live
 *  call and may genuinely not have the date yet; a hard gate turns a customer
 *  conversation into a form fight). Its whole job is to stop the absence being
 *  SILENT. Keyed on the date alone: a time without a date is not dispatchable,
 *  a date without a time is. */
export function whenMissing(journeyDate: string | null): boolean {
  return !journeyDate || journeyDate.trim() === '';
}

let options: readonly { value: string; label: string }[] | undefined;

/** The picker's options, derived once per process from the parser's own
 *  metadata: `Ireland (+353)` first, then every other country by name. */
function countryOptions(): readonly { value: string; label: string }[] {
  if (options === undefined) {
    options = listPhoneCountries().map((c) => ({
      value: c.code,
      label: `${c.name} (+${c.callingCode})`,
    }));
  }
  return options;
}

/**
 * ⭐⭐ THE ECHO LINE (board 2089 M3, R-3) — what the number will be STORED as, or
 * why it will not be. `null` means render nothing.
 *
 * ⛔⛔ THE COUNTRY IT NAMES IS THE PARSED NUMBER'S, NOT THE PICKER'S, AND THAT IS
 * THE WHOLE OF H-4. A typed `+` or `00` prefix overrides the chosen country
 * (`shared/phone.ts` documents the library's rule), so `+447400123456` under
 * Ireland is a valid United Kingdom number — and an echo built from the picker
 * would tell Helen "Ireland" about a number stored as GB, which is a confident
 * wrong answer on the screen that exists to remove one. `formatPhoneWithCountry`
 * re-reads the E.164 and answers with the number's own country.
 *
 * ⚠️ THE REFUSAL NAMES THE PICKER'S COUNTRY, and it must: there is no parsed
 * number to ask, and "not a number for X" is only actionable if it says which
 * X was tried. `parseCustomerPhone`'s refuse arm carries that region (an unknown
 * picker value reads as the Ireland default there), and `phoneCountryName`
 * always answers a name or the bare code.
 *
 * ⚠️ EMPTY IS SILENT. A blank box is not an error — it is a call Helen has not
 * taken a number on yet, and the name field's own hint already states what a
 * quote without a customer is.
 */
export function phoneEcho(phone: string, phoneCountry: string): string | null {
  const parsed = parseCustomerPhone(phone, phoneCountry);
  // THE ONE READ-BACK LINE, not a second shape of it: `+44 7400 123456 (United
  // Kingdom)`, as every other kiosk read-back and the site's echo show it. A
  // non-geographic number has no country, so it gets no parenthesis at all.
  if (parsed.ok) return formatPhoneWithCountry(parsed.e164);
  if (parsed.reason === 'empty') return null;
  // The site's capture echo (`phoneEchoText`, site/src/components/InquiryForm.astro)
  // carries this identical line; the two packages cannot share a module.
  return `That does not look like a number for ${phoneCountryName(parsed.country)}. Check it, or pick another country.`;
}
